import EmblaCarousel, {
	EmblaCarouselType,
	EmblaOptionsType,
} from 'embla-carousel'
import ClassNames from '@/scripts/vendor/embla-class-names'
import { UcoastEl } from '@/scripts/core/UcoastEl'
import { UcoastVideo } from '@/scripts/core/ucoast-video'
import { TsDOM as q } from '@/scripts/core/TsDOM'

type SlideEls = {
	slide: HTMLElement
	video: UcoastVideo
	videoEl: HTMLVideoElement
}

export class VideoTestimonialsCarousel extends UcoastEl {
	static htmlSelector = 'video-testimonials-carousel'

	private viewport?: HTMLElement
	private progressBar?: HTMLElement
	private slides: SlideEls[] = []
	private emblaApi?: EmblaCarouselType
	private activeIndex = 0
	private shouldAutoAdvance = true
	private hasStartedAutoplay = false
	private isInAutoplayRange = false
	private autoplayObserver?: IntersectionObserver
	private preloadObserver?: IntersectionObserver
	private prefersReducedMotion = window.matchMedia(
		'(prefers-reduced-motion: reduce)'
	).matches

	constructor() {
		super()

		this.viewport = q.os<HTMLElement>(
			'[data-video-testimonials-viewport]',
			this
		)
		this.progressBar = q.os<HTMLElement>(
			'[data-video-testimonials-progress]',
			this
		)

		this.slides = Array.from(
			this.querySelectorAll<HTMLElement>('[data-video-testimonial-slide]')
		).map((slide) => {
			const video = q.rs<UcoastVideo>('ucoast-video', slide)
			return {
				slide,
				video,
				videoEl: q.rs<HTMLVideoElement>('video', video),
			}
		})
	}

	override connectedCallback() {
		super.connectedCallback()

		if (!this.slides.length) return

		this.setupEmbla()
		this.bindSlides()
		this.syncUi()

		this.setupPreloadObserver()
		this.setupAutoplayObserver()
	}

	override disconnectedCallback() {
		super.disconnectedCallback()

		this.slides.forEach(({ videoEl }) => {
			videoEl.removeEventListener('play', this.handleVideoPlay)
			videoEl.removeEventListener('pause', this.handleVideoPause)
			videoEl.removeEventListener('ended', this.handleVideoEnded)
			videoEl.removeEventListener('click', this.handleVideoClick)
		})

		this.emblaApi?.destroy()
		this.emblaApi = undefined
		this.autoplayObserver?.disconnect()
		this.autoplayObserver = undefined
		this.preloadObserver?.disconnect()
		this.preloadObserver = undefined
	}

	private setupEmbla() {
		if (!this.viewport || this.slides.length < 2 || this.emblaApi) return

		const options: EmblaOptionsType = {
			align: 'start',
			containScroll: 'trimSnaps',
			dragFree: false,
			loop: this.slides.length > 3,
			slidesToScroll: 1,
		}

		try {
			this.emblaApi = EmblaCarousel(this.viewport, options, [
				ClassNames(),
			])
		} catch (error) {
			console.error(error)
		}
	}

	private bindSlides() {
		this.slides.forEach(({ video, videoEl }, index) => {
			videoEl.addEventListener('play', this.handleVideoPlay)
			videoEl.addEventListener('pause', this.handleVideoPause)
			videoEl.addEventListener('ended', this.handleVideoEnded)
			videoEl.addEventListener('click', this.handleVideoClick)
			video.dataset.slideIndex = `${index}`
			videoEl.dataset.slideIndex = `${index}`
		})
	}

	private setupAutoplayObserver() {
		if (this.prefersReducedMotion) return
		if (!('IntersectionObserver' in window)) {
			this.isInAutoplayRange = true
			this.startAutoplay()
			return
		}

		this.autoplayObserver = new IntersectionObserver(
			(entries) => {
				const entry = entries[0]
				if (!entry?.isIntersecting) {
					this.isInAutoplayRange = false
					this.pauseAllVideos()
					return
				}
				this.isInAutoplayRange = true
				this.startAutoplay()
			},
			{ rootMargin: '200px 0px 200px 0px' }
		)
		this.autoplayObserver.observe(this.viewport ?? this)
	}

	private setupPreloadObserver() {
		const connection = (
			navigator as Navigator & {
				connection?: {
					saveData?: boolean
				}
			}
		).connection
		if (connection?.saveData) return

		if (!('IntersectionObserver' in window)) {
			this.preloadVideos()
			return
		}

		this.preloadObserver = new IntersectionObserver(
			(entries) => {
				if (!entries[0]?.isIntersecting) return
				this.preloadVideos()
				this.preloadObserver?.disconnect()
				this.preloadObserver = undefined
			},
			{ rootMargin: '1000px 0px 1000px 0px' }
		)
		this.preloadObserver.observe(this.viewport ?? this)
	}

	private preloadVideos() {
		this.slides.forEach((_entry, index) => {
			this.preloadVideo(index, 'metadata')
		})
	}

	private preloadVideo(index: number, preload: 'auto' | 'metadata') {
		const target = this.slides[index]
		if (!target) return

		const { video, videoEl } = target
		videoEl.preload = preload
		if (videoEl.currentSrc || videoEl.src || !video.mp4Source) return

		videoEl.src = video.mp4Source
		video.usingMp4Source = true
		videoEl.load()
	}

	private startAutoplay() {
		if (this.hasStartedAutoplay) return
		this.hasStartedAutoplay = true
		void this.playIndex(0)
	}

	private pauseAllVideos() {
		this.slides.forEach((entry) => {
			entry.video.playEventOff()
			entry.videoEl.currentTime = 0
		})
		this.syncUi()
	}

	private handleVideoClick = (event: MouseEvent) => {
		const video = q.oct<MouseEvent, HTMLVideoElement>(event)
		if (!video) return

		const index = Number(video.dataset.slideIndex)
		if (Number.isNaN(index)) return

		if (!video.paused && !video.ended) {
			this.shouldAutoAdvance = false
			const target = this.slides[index]
			target?.video.playEventOff()
			return
		}

		this.shouldAutoAdvance = true
		void this.playIndex(index)
	}

	private handleVideoPlay = (event: Event) => {
		const video = q.ot<Event, HTMLVideoElement>(event)
		if (!video) return

		const index = Number(video.dataset.slideIndex)
		if (Number.isNaN(index)) return

		this.activeIndex = index
		this.shouldAutoAdvance = true

		this.slides.forEach((entry, entryIndex) => {
			if (entryIndex === index) return
			entry.video.playEventOff()
		})

		this.emblaApi?.scrollTo(index)
		this.syncUi()
	}

	private handleVideoPause = () => {
		this.syncUi()
	}

	private handleVideoEnded = (event: Event) => {
		const video = q.ot<Event, HTMLVideoElement>(event)
		if (!video) return

		video.currentTime = 0

		if (!this.shouldAutoAdvance || this.prefersReducedMotion) {
			this.syncUi()
			return
		}
		if (!this.isInAutoplayRange) {
			this.syncUi()
			return
		}

		const currentIndex = Number(video.dataset.slideIndex)
		if (Number.isNaN(currentIndex)) return

		const nextIndex = (currentIndex + 1) % this.slides.length
		void this.playIndex(nextIndex)
	}

	private async playIndex(index: number) {
		const target = this.slides[index]
		if (!target) return

		this.preloadVideo(index, 'auto')
		this.preloadVideo((index + 1) % this.slides.length, 'metadata')
		this.activeIndex = index
		this.shouldAutoAdvance = true
		target.video.userPaused = false
		target.videoEl.currentTime = 0

		this.slides.forEach((entry, entryIndex) => {
			if (entryIndex === index) return
			entry.video.playEventOff()
		})

		this.emblaApi?.scrollTo(index)
		this.syncUi()

		try {
			await target.video.playEventOn()
		} catch {
			this.syncUi()
		}
	}

	private syncUi() {
		this.syncProgress()

		this.slides.forEach(({ slide, videoEl }, index) => {
			const isActive = index === this.activeIndex
			const isPlaying = !videoEl.paused && !videoEl.ended

			slide.classList.toggle('is-active', isActive)
			slide.classList.toggle('is-playing', isPlaying)
		})
	}

	private syncProgress() {
		if (!this.progressBar || this.slides.length < 2) return

		const segmentWidth = 100 / this.slides.length
		const translateX = this.activeIndex * 100

		this.progressBar.style.width = `${segmentWidth}%`
		this.progressBar.style.transform = `translateX(${translateX}%)`
	}
}
